// dashboard/frontend/src/components/Campaign/CreateRoundDialog.js
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import {
    Email as EmailIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
} from '@mui/icons-material';

const CHANNEL_OPTIONS = [
    { value: 'email', label: 'Email', icon: <EmailIcon /> },
    { value: 'instagram_dm', label: 'Instagram DM', icon: <InstagramIcon /> },
    { value: 'twitter_dm', label: 'Twitter DM', icon: <TwitterIcon /> },
    { value: 'linkedin_dm', label: 'LinkedIn DM', icon: <LinkedInIcon /> },
];

const CreateRoundDialog = ({ open, onClose, onCreate, existingRounds = [] }) => {
    const [formData, setFormData] = useState({
        name: '',
        channel: 'email',
        additional_details: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value
        });
        // Clear error for this field
        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: null
            });
        }
    };

    const handleSubmit = async () => {
        // Basic validation - channel is always required
        const newErrors = {};
        
        if (!formData.channel) {
            newErrors.channel = 'Channel is required';
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Prepare data for submission
        const roundData = {
            name: formData.name || `Round ${existingRounds.length + 1}`,
            channel: formData.channel,
            additional_details: formData.additional_details,
        };

        onCreate(roundData);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setFormData({
            name: '',
            channel: 'email',
            additional_details: '',
        });
        setErrors({});
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Create New Campaign Round
                <Typography variant="body2" color="textSecondary">
                    Round {existingRounds.length + 1}
                </Typography>
            </DialogTitle>
            
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {/* Channel Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.channel}>
                            <InputLabel>Channel *</InputLabel>
                            <Select
                                value={formData.channel}
                                onChange={handleChange('channel')}
                                label="Channel *"
                            >
                                {CHANNEL_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {option.icon}
                                            <Typography>{option.label}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Round Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Round Name (Optional)"
                            value={formData.name}
                            onChange={handleChange('name')}
                            helperText="Leave empty to auto-generate"
                            error={!!errors.name}
                        />
                    </Grid>

                    {/* Additional Details */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Additional Details"
                            value={formData.additional_details}
                            onChange={handleChange('additional_details')}
                            multiline
                            rows={4}
                            helperText="Round-specific context. Leave empty to use campaign defaults"
                            error={!!errors.additional_details}
                        />
                    </Grid>
                </Grid>
                
                {/* Info Box */}
                <Alert severity="info" sx={{ mt: 3 }}>
                    {formData.channel === 'email' ? (
                        <>Configure Gmail accounts, subject line, and signature after creating the round.</>
                    ) : (
                        <>Configure channel-specific settings after creating the round.</>
                    )}
                </Alert>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained"
                    color="primary"
                >
                    Create Round
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRoundDialog;